import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import type { Activity, ActivityFlowConsents } from "@lib/activity";
import type { ConfirmationConsentsBlockType } from "@lib/config/types";
import type { DoubleOptinTicket, Ticket } from "@lib/ticket";
import {
  alignStyle,
  type FontFamily,
  resolveFontFamily,
  textBlockWidth,
} from "@lib/utils";
import type { CheckedState } from "@radix-ui/react-checkbox";
import { useEffect, type CSSProperties } from "react";
import { useFormContext } from "react-hook-form";
import type { FlowType } from "./blocks";
import { useFormState } from "../forms/landing/form-state-context";

type ConfirmationConsentsProps = ConfirmationConsentsBlockType & {
  activity: Activity;
  ticket?: Ticket;
  flowConsents?: ActivityFlowConsents;
  flowType?: FlowType;
};

type ConfirmationConsentsStyles = CSSProperties & {
  "--anchor-color": string;
  "--checkbox-color": string;
  "--checkbox-border-color": string;
  "--input-background": string;
  "--checkbox-border-width": string;
  "--checkbox-border-radius": string;
};
export function ConfirmationConsentsBlock({
  confirmation_consents: blockConfig,
  flowConsents,
  ...props
}: ConfirmationConsentsProps) {
  const { register } = useFormContext();
  const fontFamily = blockConfig.label.font as FontFamily;
  const ticket = props.ticket as DoubleOptinTicket;

  // Compute a safe maxWidth string and clamp it to 100% of the viewport.
  const widthOption = blockConfig.width ?? "medium";
  const computedTextWidth = textBlockWidth(widthOption);
  const maxWidthClamped = `min(${computedTextWidth}, 100%)`;

  return (
    <div
      data-block-type="ConfirmationConsents"
      className="[&_a]:text-[var(--anchor-color)] [&_a]:underline"
      style={{
        ...alignStyle(blockConfig),
        // clamp block width to viewport and ensure it respects box sizing
        maxWidth: maxWidthClamped,
        width: "100%",
        boxSizing: "border-box",
        // allow long labels/links to break and not create overflow
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      }}
    >
      <input
        type="hidden"
        value="true"
        {...register("has_confirmation_consents")}
      />
      <div
        className="grid gap-8"
        style={
          {
            ...(blockConfig.alignment === "right" && { justifyContent: "end" }),
            fontSize: blockConfig.label.font_size,
            ...resolveFontFamily(fontFamily, blockConfig.label.font_properties),
            color: blockConfig.label.color,
            "--anchor-color": blockConfig.label.hyperlink_color,
            "--checkbox-color": blockConfig.checkbox.color,
            "--checkbox-border-color": blockConfig.checkbox.border.color,
            "--checkbox-border-width": `${blockConfig.checkbox.border.width}px`,
            "--input-background": blockConfig.checkbox.background_color,
            "--checkbox-border-radius": `${blockConfig.checkbox.border.radius}px`,
          } as ConfirmationConsentsStyles
        }
      >
        {flowConsents && flowConsents.length > 0 && (
          <ConfirmationConsentList
            flowConsents={flowConsents}
            ticket={ticket}
          />
        )}
      </div>
    </div>
  );
}

function ConfirmationConsentList({
  flowConsents,
  ticket,
}: {
  flowConsents: ActivityFlowConsents;
  ticket: DoubleOptinTicket;
}) {
  const { watch } = useFormContext();
  const { setDisableSubmit } = useFormState();
  const approvedConsents = flowConsents
    .filter((consent) => consent.doubleOptIn)
    .filter((consent) =>
      ticket?.approved_consents?.map((c) => c.consent_id).includes(consent.id),
    );
  const formValues = watch();

  const confirmedConsents = Object.entries(formValues).filter(
    ([key, value]) => key.startsWith("confirmation_consent:") && value === true,
  );
  useEffect(() => {
    // Disable submit button when there's more than
    // one consent and nothing is checked
    const disableSubmit =
      approvedConsents.length > 1 && confirmedConsents.length === 0;
    setDisableSubmit(disableSubmit);
  }, [formValues]);

  return approvedConsents.map((consent) => {
    const orderConsentId = ticket?.approved_consents.find(
      (c) => c.consent_id === consent.id,
    )?.order_consent_id;

    const multipleConsents = approvedConsents.length > 1;

    return (
      <ConfirmationConsent
        key={consent.id}
        consent={consent}
        orderConsentId={orderConsentId}
        showCheckbox={multipleConsents}
        preChecked={!multipleConsents}
        showConsentHandlerTitle={approvedConsents.length > 1}
      />
    );
  });
}

type ConfirmationConsentProps = {
  consent: ActivityFlowConsents[number];
  orderConsentId?: string;
  showCheckbox: boolean;
  preChecked: boolean;
  showConsentHandlerTitle: boolean;
};

function ConfirmationConsent({
  consent,
  orderConsentId,
  showCheckbox,
  preChecked,
  showConsentHandlerTitle,
}: ConfirmationConsentProps) {
  const { control, setValue } = useFormContext();

  useEffect(() => {
    if (preChecked) {
      setValue(`confirmation_consent:${orderConsentId}`, true);
    }
  });

  // Fallback to standard label if confirmationLabel is not present.
  const label = consent?.confirmationLabel ?? consent.label;

  return (
    <FormField
      control={control}
      name={`confirmation_consent:${orderConsentId}`}
      defaultValue={preChecked}
      render={({ field }) => (
        <FormItem key={field.name} className="grid gap-3">
          {showConsentHandlerTitle && (
            <h2 className="font-bold text-xl">{consent.handler}</h2>
          )}

          {/* Layout adjusted to be responsive:
              - stack on small screens, row on >=sm
              - ensure text container can shrink with min-w-0
              - ensure checkboxes don't force layout width */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex-none">
              <FormControl>
                <Checkbox
                  className={showCheckbox ? "" : "hidden"}
                  id={field.name}
                  name={field.name}
                  checked={field.value as CheckedState | undefined}
                  onCheckedChange={field.onChange}
                  aria-labelledby={`label-${consent.id}`}
                />
              </FormControl>
            </div>

            <div className="flex-1 min-w-0 grid gap-2 leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {label && (
                <label
                  htmlFor={`confirmation_consent:${orderConsentId}`}
                  id={`label-${consent.id}`}
                  className="flex min-h-[1.5em] items-center font-medium break-words"
                  dangerouslySetInnerHTML={{
                    __html: label ?? "",
                  }}
                />
              )}
            </div>
          </div>

          <FormMessage />
        </FormItem>
      )}
    />
  );
}
